import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_ALLOW_LISTS_RESOURCE } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowLists';
import { useUpdateAllowList } from 'src/features/parkingManagement/api/AllowListAPI/useUpdateAllowList';
import AllowListFormModal from 'src/features/parkingManagement/components/AllowLists/forms/AllowListFormModal';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { type DataTableNewEntryProps } from 'src/registry/components/table/types';
import { toast } from 'src/registry/hooks/UseToast';
import { generalMessages } from 'src/utils/messages';

import type { AllowList } from 'src/features/parkingManagement/types/allowList';

interface AllowListFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedList: AllowList;
    children?: React.ReactNode;
}

export default function UpdateAllowListFormModal<TData>({
    className,
    semantics,
    children,
    selectedList,
}: AllowListFormModalProps<TData>) {
    const { parkingId } = useParking();
    const { formatMessage } = useIntl();
    const { mutate, isPending } = useUpdateAllowList({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LISTS_RESOURCE] });
            toast({
                title: formatMessage(allowListMessages.updateAllowListSuccess),
                description: formatMessage(allowListMessages.updateAllowListSuccessDescription, {
                    entry: name,
                }),
            });
        },
    });

    return (
        <AllowListFormModal
            selectedList={selectedList}
            className={className}
            mutate={(values, onSuccess) => {
                mutate({ parkingId, listId: selectedList.id, ...values }, { onSuccess });
            }}
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(allowListMessages.editAllowListSubtitle, {
                entry: selectedList.name,
            })}
        >
            {children}
        </AllowListFormModal>
    );
}
